import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { StaxLogo } from "@/components/stax/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLES = {
  draft: "bg-white text-ink",
  sent: "bg-staxyellow text-ink",
  paid: "bg-staxcyan text-ink",
};

export default function Invoice() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [missing, setMissing] = useState(false);
  const [paying, setPaying] = useState(false);
  const sessionId = searchParams.get("session_id");
  const canceled = searchParams.get("canceled");

  const load = () =>
    axios.get(`${API}/invoice/${id}`).then((r) => setInv(r.data)).catch(() => setMissing(true));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Returning from Stripe checkout — confirm the payment before showing paid.
  useEffect(() => {
    if (!sessionId || !id) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/invoice/${id}/payment-status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          await load();
          toast.success("Payment received — thank you!");
          setSearchParams({}, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      if (++tries < 12) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, id]);

  useEffect(() => {
    if (canceled) {
      toast.error("Payment canceled — no charge was made.");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canceled]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/invoice/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  return (
    <div data-testid="invoice-page" className="min-h-screen bg-paper px-4 py-12 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <StaxLogo size={44} />
          <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.3em] text-ink/40">Invoice</span>
        </div>

        {missing && (
          <div className="mt-10 border-4 border-ink bg-white p-10 text-center shadow-hard">
            <p className="font-display uppercase text-3xl">Invoice not found</p>
            <p className="mt-2 text-sm text-ink/60">Double-check the link from your email.</p>
          </div>
        )}

        {inv && (
          <div className="mt-10 border-4 border-ink bg-white shadow-hard">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b-4 border-ink bg-staxyellow px-6 py-4">
              <p data-testid="invoice-number" className="font-display uppercase text-2xl">{inv.number}</p>
              <span data-testid="invoice-status" className={`border-2 border-ink px-3 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] ${STATUS_STYLES[inv.status] || STATUS_STYLES.draft}`}>
                {inv.status}
              </span>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/45">Billed to</p>
                  <p className="mt-1 font-display uppercase text-xl">{inv.client_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/45">Date</p>
                  <p className="mt-1 text-sm font-semibold">{(inv.created_at || "").slice(0, 10)}</p>
                  {inv.due_date && (
                    <>
                      <p className="mt-3 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/45">Due</p>
                      <p className="mt-1 text-sm font-semibold">{inv.due_date}</p>
                    </>
                  )}
                </div>
              </div>

              <table className="mt-8 w-full border-4 border-ink text-sm">
                <thead>
                  <tr className="bg-ink text-white">
                    <th className="px-4 py-2.5 text-left font-display uppercase text-xs tracking-wide">Item</th>
                    <th className="px-4 py-2.5 text-center font-display uppercase text-xs tracking-wide">Qty</th>
                    <th className="px-4 py-2.5 text-right font-display uppercase text-xs tracking-wide">Price</th>
                    <th className="px-4 py-2.5 text-right font-display uppercase text-xs tracking-wide">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.items.map((i, idx) => (
                    <tr key={idx} className="border-t-2 border-ink/15">
                      <td className="px-4 py-3 font-semibold">{i.desc}</td>
                      <td className="px-4 py-3 text-center">{i.qty || 1}</td>
                      <td className="px-4 py-3 text-right">${(i.price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold">${(((i.qty || 1) * (i.price || 0))).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-4 border-ink bg-staxyellow">
                    <td colSpan="3" className="px-4 py-3 font-display uppercase text-lg">Total</td>
                    <td data-testid="invoice-total" className="px-4 py-3 text-right font-display text-2xl">${(inv.total ?? 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>

              {inv.status === "sent" && (
                <button
                  data-testid="invoice-pay-button"
                  onClick={pay}
                  disabled={paying}
                  className="mt-8 flex w-full items-center justify-center gap-2 border-4 border-ink bg-staxmagenta px-8 py-4 font-display uppercase text-xl text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  <CreditCard size={20} /> {paying ? "Redirecting to checkout..." : `Pay $${(inv.total ?? 0).toFixed(2)} Now`}
                </button>
              )}
              {inv.status === "sent" && (
                <p className="mt-2 text-center font-mono2 text-[10px] uppercase tracking-widest text-ink/40">
                  Secure card payment powered by Stripe
                </p>
              )}

              {inv.notes && <p className="mt-6 text-sm font-medium text-ink/65 leading-relaxed">{inv.notes}</p>}
              {inv.status === "paid" && (
                <p className="mt-6 inline-block border-2 border-ink bg-staxcyan px-4 py-2 font-display uppercase text-sm">Paid — thank you!</p>
              )}
            </div>
          </div>
        )}

        <p className="mt-8 text-center font-mono2 text-[10px] uppercase tracking-[0.3em] text-ink/40">
          STAX MARKETING LLC — Creative design &amp; marketing studio
        </p>
      </div>
    </div>
  );
}
